import { CircularProgress, Tooltip } from '@mui/material';
import {
  Cancel,
  CheckCircle,
  CheckCircleOutlined,
  ErrorOutlined,
  RadioButtonUnchecked,
  ReportProblemOutlined
} from '@mui/icons-material';
import { KnownBatchRowOutcome } from '@groundfloor-platform/mgmt-service-g-sdk';
import { ReactNode } from 'react';
import { useTooltipStyles } from '../../../../../../ui/components/pure/list-table/list-table.styles';
import { Colors } from '../../../../../../ui/theme/colors';
import { CourierBatchRow } from '../../../../logic/courier-batch.map';

const ICON_SIZE = '18px';

// What a validated row would become, in the prepare's own words.
const actionLabels: Record<string, string> = {
  add: 'Add',
  update: 'Update',
  'no-change': 'No change'
};

const outcomeLabels: Record<string, string> = {
  [KnownBatchRowOutcome.Added]: 'Added',
  [KnownBatchRowOutcome.Updated]: 'Updated',
  [KnownBatchRowOutcome.NoChange]: 'No change'
};

export function CourierBatchRowStatus({ row, isValidating = false }: { row: CourierBatchRow; isValidating?: boolean }) {
  const tooltipClasses = useTooltipStyles();
  const { icon, label } = isValidating
    ? { icon: <CircularProgress size={16} aria-label="Validating" />, label: 'Validating' }
    : describeStatus(row);
  const messages = isValidating
    ? []
    : [...row.diagnostics.map((diagnostic) => diagnostic.message), row.result?.reason].filter(
        (message): message is string => !!message
      );

  const content = (
    // Focusable when it has messages, so they reach a keyboard as well as a pointer.
    <span
      tabIndex={messages.length > 0 ? 0 : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
    >
      {icon}
      {label}
    </span>
  );

  if (messages.length === 0) {
    return content;
  }

  return (
    <Tooltip
      arrow
      classes={tooltipClasses}
      title={
        <ul style={{ margin: 0, paddingLeft: '16px' }}>
          {messages.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      }
    >
      {content}
    </Tooltip>
  );
}

function describeStatus(row: CourierBatchRow): { icon: ReactNode; label: string } {
  const iconStyle = (color: string) => ({ color, fontSize: ICON_SIZE });

  switch (row.status) {
    case 'ready':
      return {
        icon: <CheckCircleOutlined style={iconStyle(Colors.collectedGreen)} />,
        label: actionLabels[row.action ?? ''] ?? 'Ready'
      };
    case 'warning':
      return { icon: <ReportProblemOutlined style={iconStyle(Colors.overdueOrange)} />, label: 'Warning' };
    case 'error':
      return { icon: <ErrorOutlined style={iconStyle(Colors.cancelledDeletedRed)} />, label: 'Error' };
    case 'edited':
      return { icon: <RadioButtonUnchecked style={iconStyle(Colors.midGray)} />, label: 'Not validated' };
    case 'uploading':
      return { icon: <CircularProgress size={16} aria-label="Uploading" />, label: 'Uploading' };
    case 'uploaded':
      return {
        icon: <CheckCircle style={iconStyle(Colors.collectedGreen)} />,
        label: outcomeLabels[row.result?.outcome ?? ''] ?? 'Uploaded'
      };
    case 'failed':
    default:
      return { icon: <Cancel style={iconStyle(Colors.cancelledDeletedRed)} />, label: 'Failed' };
  }
}
